import type { EntitySnapshot } from './protocol';

/**
 * Puts back the fields the server stopped repeating.
 *
 * ── What the server does, and why ────────────────────────────────────────────
 *
 * An entity's `kind`, `maxHp`, `name`, `rank`, `traits`, `mesh`, `armour` and `weapon` never
 * change for its life. Re-sending them thirty times a second was **51% of the frame** —
 * measured at 85 of 167 bytes per entity on a level-50 peak — so the server describes an
 * entity the first tick it appears in an instance's snapshot and afterwards sends only what
 * moves. This is the other half of that arrangement.
 *
 * ── Why a cache and not "the last snapshot" ──────────────────────────────────
 *
 * The obvious implementation is to merge each frame onto the previous one. It is wrong for
 * a reason that only shows up in play: an entity that leaves the frame and comes back — a
 * monster walking out of `MobVisionRadius` and in again — is not re-described, because the
 * *server* still remembers describing it. Merging against the previous frame would have
 * lost it in between and left the monster nameless on its return. The cache is keyed by id
 * and outlives absence, which is exactly the server's own bookkeeping mirrored.
 *
 * ── When it is thrown away ───────────────────────────────────────────────────
 *
 * On a zone change and on a reconnect, because the ids do not survive either. The server
 * re-describes at exactly those two moments (`Instance.forgetDescriptions`), so the two
 * halves agree by construction rather than by timing.
 */

/** The fields that ride only the first frame an entity appears in. */
type Static = Pick<
  EntitySnapshot,
  | 'kind'
  | 'maxHp'
  | 'name'
  | 'rank'
  | 'traits'
  | 'mesh'
  | 'armour'
  | 'weapon'
  | 'head'
  | 'hands'
  | 'legs'
  | 'feet'
  | 'offhand'
>;

const STATIC_KEYS = [
  'kind',
  'maxHp',
  'name',
  'rank',
  'traits',
  'mesh',
  'armour',
  'weapon',
  'head',
  'hands',
  'legs',
  'feet',
  'offhand',
] as const;

export class EntityDescriptions {
  private readonly byId = new Map<string, Static>();

  /**
   * Fills an entity in from what has been seen before, and remembers anything new.
   *
   * Mutates the snapshot in place. It is a freshly-parsed wire object with exactly one
   * reader, and copying every entity to add four fields would allocate a second array
   * thirty times a second for nothing.
   */
  rehydrate(entities: EntitySnapshot[]): EntitySnapshot[] {
    for (const e of entities) {
      const known = this.byId.get(e.id);
      let described = known;

      // A frame that carries any static field is a description: record all of them.
      // `kind` is the discriminator the server always includes in a description, so it
      // is what decides — checking each field separately would let a partial frame
      // overwrite a good cache entry with undefined.
      if (e.kind !== undefined) {
        described = {
          kind: e.kind,
          maxHp: e.maxHp,
          name: e.name,
          rank: e.rank,
          traits: e.traits,
          mesh: e.mesh,
          armour: e.armour,
          weapon: e.weapon,
          head: e.head,
          hands: e.hands,
          legs: e.legs,
          feet: e.feet,
          offhand: e.offhand,
        };
        this.byId.set(e.id, described);
        continue;
      }

      // Otherwise fill from the cache. An entity with no description on either side is
      // left as it arrived: the renderer's own fallbacks handle a missing mesh or name,
      // and inventing values here would hide the fault rather than report it.
      if (!described) continue;
      for (const key of STATIC_KEYS) {
        if (e[key] === undefined) {
          // One assignment per field rather than a spread: the object is reused by the
          // caller and replacing it would break the `raw` map's identity in the store.
          (e as unknown as Record<string, unknown>)[key] = described[key];
        }
      }
    }
    return entities;
  }

  /** Forgets an entity, so the next description is taken as new. */
  forget(id: string): void {
    this.byId.delete(id);
  }

  /**
   * Throws the lot away — on a zone change or a reconnect.
   *
   * The server re-describes at exactly those two moments, so clearing here is not merely
   * safe, it is the half that makes the pair correct.
   */
  clear(): void {
    this.byId.clear();
  }

  /** How many entities are described. For tests, and for a leak that would show as growth. */
  get size(): number {
    return this.byId.size;
  }
}


/**
 * Turns a wire snapshot into the whole world.
 *
 * ── Why this exists, and why it wraps the descriptions rather than sitting beside them ──
 *
 * The server sends two kinds of economy in the same frame, and both mean "you already know
 * this": an entity's **static fields** ride only its first frame, and from the delta pass an
 * **unchanged entity is omitted entirely**. Neither is something the rest of the client
 * should ever have to think about — the interpolation buffer wants a complete entity map per
 * frame to interpolate between, the store's removal rule is *absent means gone*, and the
 * render loop disposes a mesh the moment its id stops appearing.
 *
 * So the whole world is rebuilt here, once, at the edge. Everything downstream keeps working
 * on complete snapshots and none of it knows the wire got smaller.
 *
 * ── Absence stopped meaning removal, so removal had to be said out loud ─────
 *
 * That is the one rule the delta breaks. `payload.gone` carries the ids that actually left,
 * and it is the *only* thing that removes an entity now. Inferring it from absence — which
 * every layer below used to do — would delete a monster the instant it stood still.
 *
 * ── Unchanged entities keep their previous object ───────────────────────────
 *
 * Deliberately, and it is why a changed entity **replaces** rather than mutates: the
 * interpolation buffer holds references to the entity maps of older frames, so mutating one
 * in place would rewrite history and make the interpolation sample a position that was never
 * sent. Replacing leaves every past frame telling the truth.
 */
export class SnapshotAssembler {
  private readonly descriptions = new EntityDescriptions();
  private readonly live = new Map<string, EntitySnapshot>();

  /** The complete entity list, from a frame carrying only what changed. */
  assemble(entities: EntitySnapshot[], gone?: readonly string[]): EntitySnapshot[] {
    for (const e of this.descriptions.rehydrate(entities)) {
      // Replaced, never merged into the previous object — see the note above.
      this.live.set(e.id, e);
    }
    for (const id of gone ?? []) {
      this.live.delete(id);
      this.descriptions.forget(id);
    }
    return [...this.live.values()];
  }

  /** Throws the world away — on a zone change or a reconnect, where ids do not survive. */
  clear(): void {
    this.descriptions.clear();
    this.live.clear();
  }

  /** How many entities are being tracked. For tests, and for a leak that shows as growth. */
  get size(): number {
    return this.live.size;
  }
}
